/**
 * Template Preview
 * Shows a live iframe preview of the email template with applied variables
 *
 * @file src/components/email/templates/TemplatePreview.tsx
 * @created 2025-10-22
 * @updated 2025-10-24 - Added live iframe rendering with debounced updates
 */

import React, { useState, useEffect, useRef } from 'react';
import EmailService, { EmailTemplate } from '@/api/email';
import { Loader2, Eye, Monitor, Smartphone, Tablet, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TemplatePreviewProps {
  template: EmailTemplate;
  variables: {
    primaryColor: string;
    secondaryColor: string;
    primaryTextColor: string;
    secondaryTextColor: string;
    headerLogo: string | null;
    footerLogo?: string | null;
  };
  debounceMs?: number;
}

type DeviceSize = 'mobile' | 'tablet' | 'desktop';

/**
 * TemplatePreview Component
 *
 * Displays a live iframe preview of the email template with customized colors/logos.
 * Features:
 * - Real-time preview updates (debounced)
 * - Device size toggles
 * - Loading states
 * - Error handling
 *
 * @example
 * <TemplatePreview
 *   template={template}
 *   variables={{ primaryColor, secondaryColor, primaryTextColor, secondaryTextColor, headerLogo }}
 *   debounceMs={300}
 * />
 */
const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  template,
  variables,
  debounceMs = 300
}) => {
  const [previewHtml, setPreviewHtml] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Get device width based on selected size
  const getDeviceWidth = () => {
    switch (deviceSize) {
      case 'mobile':
        return 'w-[375px]'; // iPhone SE width
      case 'tablet':
        return 'w-[768px]'; // iPad width
      case 'desktop':
        return 'w-full';
    }
  };

  // Debounced preview generation
  useEffect(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set loading state immediately
    setLoading(true);

    // Debounce the API call
    timeoutRef.current = setTimeout(async () => {
      try {
        console.log('[TemplatePreview] Fetching preview with variables:', variables);

        const response = await EmailService.getTemplatePreview(template.id, variables);

        console.log('[TemplatePreview] Preview HTML received, length:', response.html.length);

        setPreviewHtml(response.html);
        setError(null);
      } catch (err: any) {
        console.error('[TemplatePreview] Error fetching preview:', err);
        setError(err.message || 'Failed to load preview');
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [template.id, variables, debounceMs]);

  // Error state
  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          {error}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Preview Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">Live Preview</span>
        </div>

        {loading && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Updating...</span>
          </div>
        )}
      </div>

      {/* Device Size Toggle */}
      <div className="flex gap-2">
        <Button
          variant={deviceSize === 'mobile' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDeviceSize('mobile')}
          className={cn("gap-2 flex-1", deviceSize === 'mobile' && "[&>*]:!text-white")}
        >
          <Smartphone className="h-4 w-4" />
          <span className="hidden sm:inline">Mobile</span>
        </Button>
        <Button
          variant={deviceSize === 'tablet' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDeviceSize('tablet')}
          className={cn("gap-2 flex-1", deviceSize === 'tablet' && "[&>*]:!text-white")}
        >
          <Tablet className="h-4 w-4" />
          <span className="hidden sm:inline">Tablet</span>
        </Button>
        <Button
          variant={deviceSize === 'desktop' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setDeviceSize('desktop')}
          className={cn("gap-2 flex-1", deviceSize === 'desktop' && "[&>*]:!text-white")}
        >
          <Monitor className="h-4 w-4" />
          <span className="hidden sm:inline">Desktop</span>
        </Button>
      </div>

      {/* Preview Iframe */}
      <div className="flex-1 flex items-start justify-center bg-gray-100 rounded-lg overflow-auto p-4">
        {loading && !previewHtml ? (
          <div className="flex flex-col items-center gap-2 mt-20">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            <p className="text-sm text-gray-500">Loading preview...</p>
          </div>
        ) : (
          <div
            className={cn(
              'border rounded-lg overflow-hidden bg-white shadow-lg transition-all duration-300',
              getDeviceWidth()
            )}
          >
            <iframe
              srcDoc={previewHtml}
              className="w-full border-0"
              title="Template Preview"
              sandbox="allow-same-origin"
              style={{
                height: '800px',
              }}
            />
          </div>
        )}
      </div>

      {/* Preview Note */}
      <p className="text-xs text-gray-500 text-center">
        This is a live preview showing your customizations. Changes may take a moment to update.
      </p>
    </div>
  );
};

export default TemplatePreview;
